// What a user has done with dashboard FAB videos, per browser. vod decides which
// videos may show on a page; this only removes the ones the user is done with.
const storageKey = (uid) => `dashboardFab:${uid}`;

const EMPTY_STATE = { watched: {}, dismissed: {} };

export const readDashboardFabState = (uid) => {
  if (!uid) return EMPTY_STATE;
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey(uid)));
    return {
      watched: stored?.watched ?? {},
      dismissed: stored?.dismissed ?? {},
    };
  } catch {
    return EMPTY_STATE;
  }
};

const record = (uid, field, videoIds) => {
  if (!uid || !videoIds.length) return;
  const state = readDashboardFabState(uid);
  const now = Date.now();
  const updated = { ...state, [field]: { ...state[field] } };
  videoIds.forEach((videoId) => {
    updated[field][videoId] = now;
  });
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify(updated));
  } catch {
    // Storage full or blocked: the video shows again next time, which is harmless.
  }
};

export const recordWatched = (uid, videoId) => record(uid, 'watched', [videoId]);

export const recordDismissed = (uid, videoIds) => record(uid, 'dismissed', videoIds);

// Seconds of playback in the opened FAB that make a video watched.
export const WATCHED_AFTER_SECONDS = 2;

/**
 * Adds up how long each video plays in the opened FAB, from the embed's
 * `timeupdate` events, and calls `onWatched` once per video when it reaches
 * WATCHED_AFTER_SECONDS. Previews in the collapsed FAB do not count, and
 * neither do jumps such as a seek or a restart.
 */
export const createWatchTracker = (onWatched) => {
  const progress = {};
  return ({ videoId, currentTime, expanded } = {}) => {
    if (!videoId || typeof currentTime !== 'number') return;
    if (!progress[videoId]) progress[videoId] = { lastTime: undefined, seconds: 0, isWatched: false };
    const entry = progress[videoId];
    const delta = currentTime - entry.lastTime;
    entry.lastTime = currentTime;
    if (!expanded || entry.isWatched || !(delta > 0 && delta < 1)) return;
    entry.seconds += delta;
    if (entry.seconds >= WATCHED_AFTER_SECONDS) {
      entry.isWatched = true;
      onWatched(videoId);
    }
  };
};

/**
 * The ids of the videos to put in the FAB, in the order vod returned them:
 * a watched video drops out if its rules say so, and so does a dismissed one.
 */
export const selectDashboardFabVideoIds = (videos = [], state = EMPTY_STATE) => videos
  .filter(({ id, hideWhenWatched, hideWhenDismissed }) => !(hideWhenWatched && state.watched[id])
    && !(hideWhenDismissed && state.dismissed[id]))
  .map(({ id }) => id);
